/*
  Blog post page for FullStack.cash - renders markdown posts
*/

import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, Link } from 'react-router-dom'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { ArrowLeft } from 'react-bootstrap-icons'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

function BlogPost () {
  const { slug } = useParams()
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('')
  const [date, setDate] = useState('')

  useEffect(() => {
    const loadMarkdown = async () => {
      try {
        const markdownModule = await import(`../markdown/blog/${slug}.md`)
        const response = await fetch(markdownModule.default)
        const text = await response.text()
        
        // Parse frontmatter
        const frontmatterMatch = text.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/)
        if (frontmatterMatch) {
          const frontmatter = frontmatterMatch[1]
          const contentText = frontmatterMatch[2]
          
          const titleMatch = frontmatter.match(/title:\s*['"](.+?)['"]/)
          const dateMatch = frontmatter.match(/date:\s*['"](.+?)['"]/)
          
          if (titleMatch) setTitle(titleMatch[1])
          if (dateMatch) {
            const dateObj = new Date(dateMatch[1])
            setDate(dateObj.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            }))
          }
          setContent(contentText)
        } else {
          setContent(text)
        }
      } catch (err) {
        console.error('Error loading markdown:', err)
        setContent('# Post Not Found\n\nThe blog post you are looking for does not exist.')
      } finally {
        setLoading(false)
      }
    }

    if (slug) {
      loadMarkdown()
    }
  }, [slug])

  if (loading) {
    return (
      <Container className='py-5'>
        <Row>
          <Col md={{ span: 8, offset: 2 }} className='text-center'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>{title ? `${title} - FullStack.cash` : 'Blog Post - FullStack.cash'}</title>
      </Helmet>

      {/* Back Navigation */}
      <Container className='pt-4'>
        <Row>
          <Col md={{ span: 8, offset: 2 }}>
            <Link to='/blog' className='text-decoration-none'>
              <Button variant='outline-secondary' size='sm' className='d-inline-flex align-items-center gap-2'>
                <ArrowLeft />
                Back to Blog
              </Button>
            </Link>
          </Col>
        </Row>
      </Container>

      {/* Article Header */}
      {(title || date) && (
        <Container className='pt-4 pb-3'>
          <Row>
            <Col md={{ span: 8, offset: 2 }} className='text-center'>
              {date && (
                <p className='text-muted mb-2' style={{ fontSize: '0.875rem', letterSpacing: '0.05em' }}>
                  {date}
                </p>
              )}
              {title && (
                <h1 className='display-5 fw-bold' style={{ lineHeight: '1.3' }}>
                  {title}
                </h1>
              )}
              <hr className='mt-4 mb-0' />
            </Col>
          </Row>
        </Container>
      )}

      {/* Article Content */}
      <Container className='py-4'>
        <Row>
          <Col md={{ span: 8, offset: 2 }}>
            <article 
              className='blog-post-content'
              style={{
                fontSize: '1.125rem',
                lineHeight: '1.8',
                color: '#333'
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => (
                    <h1 style={{ 
                      fontSize: '2rem', 
                      fontWeight: '700', 
                      marginBottom: '1.5rem',
                      marginTop: '2rem',
                      lineHeight: '1.3'
                    }}>
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2 style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: '600', 
                      marginBottom: '1rem',
                      marginTop: '2rem',
                      lineHeight: '1.4',
                      borderBottom: '1px solid #e9ecef',
                      paddingBottom: '0.5rem'
                    }}>
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3 style={{ 
                      fontSize: '1.25rem', 
                      fontWeight: '600', 
                      marginBottom: '0.75rem',
                      marginTop: '1.5rem',
                      lineHeight: '1.4'
                    }}>
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p style={{ 
                      marginBottom: '1.25rem',
                      lineHeight: '1.8'
                    }}>
                      {children}
                    </p>
                  ),
                  ul: ({ children }) => (
                    <ul style={{ 
                      marginBottom: '1.25rem',
                      paddingLeft: '1.5rem'
                    }}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol style={{ 
                      marginBottom: '1.25rem',
                      paddingLeft: '1.5rem'
                    }}>
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li style={{ 
                      marginBottom: '0.5rem',
                      lineHeight: '1.7'
                    }}>
                      {children}
                    </li>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ fontWeight: '600' }}>{children}</strong>
                  ),
                  a: ({ href, children }) => (
                    <a 
                      href={href}
                      style={{ 
                        color: '#0d6efd',
                        textDecoration: 'none',
                        borderBottom: '1px solid transparent',
                        transition: 'border-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.borderBottomColor = '#0d6efd'}
                      onMouseLeave={(e) => e.target.style.borderBottomColor = 'transparent'}
                    >
                      {children}
                    </a>
                  ),
                  hr: () => (
                    <hr style={{ 
                      margin: '2rem 0',
                      border: 'none',
                      borderTop: '1px solid #dee2e6'
                    }} />
                  ),
                  table: ({ children }) => (
                    <div className='table-responsive mb-4'>
                      <table className='table table-bordered table-hover' style={{ marginBottom: '0' }}>
                        <tbody>{children}</tbody>
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => <thead style={{ backgroundColor: '#f8f9fa' }}>{children}</thead>,
                  tbody: ({ children }) => <tbody>{children}</tbody>,
                  th: ({ children }) => (
                    <th style={{ 
                      backgroundColor: '#f8f9fa',
                      fontWeight: '600',
                      padding: '0.75rem'
                    }}>
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td style={{ padding: '0.75rem' }}>
                      {children}
                    </td>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote style={{
                      borderLeft: '4px solid #0d6efd',
                      paddingLeft: '1rem',
                      marginLeft: '0',
                      marginBottom: '1.25rem',
                      fontStyle: 'italic',
                      color: '#6c757d'
                    }}>
                      {children}
                    </blockquote>
                  )
                }}
              >
                {content}
              </ReactMarkdown>
            </article>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default BlogPost
